import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Paper, TextField, Button, CircularProgress, Alert,
  Grid, Divider, Chip
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const UserProfile = () => {
  const { user, updateUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);

  // Load current profile
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/auth/me');
        setCurrentProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile data');
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const validationSchema = Yup.object({
    full_name: Yup.string().required('Full name is required'),
    phone: Yup.string().nullable()
  });

  const formik = useFormik({
    initialValues: {
      full_name: currentProfile?.full_name || '',
      phone: currentProfile?.phone || ''
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        // Update profile
        await api.put('/api/auth/profile', values);
        
        // Update local user state
        updateUserProfile(values);
        
        toast.success('Profile updated successfully');
      } catch (error) {
        console.error('Error updating profile:', error);
        setError(error.response?.data?.error || 'Failed to update profile');
        toast.error('Failed to update profile');
      } finally {
        setLoading(false);
      }
    }
  });

  // Role display helper
  const getRoleDisplay = (role, isAssociationAdmin) => {
    if (role === 'superadmin') return 'Super Admin';
    if (role === 'subadmin' && isAssociationAdmin) return 'Association Admin';
    if (role === 'subadmin') return 'Sub Admin';
    if (role === 'viewer') return 'Viewer';
    return role;
  };

  if (loading && !currentProfile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>User Profile</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={4}>
        {/* Account Information */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Account Information</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Username</Typography>
              <Typography variant="body1">{currentProfile?.username}</Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Email</Typography>
              <Typography variant="body1">{currentProfile?.email}</Typography>
            </Box>
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Role</Typography>
              <Chip 
                label={getRoleDisplay(currentProfile?.role, currentProfile?.is_association_admin)} 
                color="primary" 
                size="small" 
              />
            </Box>
            
            {currentProfile?.association && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Association</Typography>
                <Typography variant="body1">{currentProfile.association.name}</Typography>
              </Box>
            )}

            {currentProfile?.role === 'subadmin' && currentProfile?.distributor_name && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Distributor Name</Typography>
                <Typography variant="body1">{currentProfile.distributor_name}</Typography>
              </Box>
            )}
            
            {currentProfile?.role === 'viewer' && currentProfile?.created_by && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Linked to Distributor</Typography>
                <Typography variant="body1">
                  {currentProfile.created_by.distributor_name || 
                   currentProfile.created_by.full_name || 
                   currentProfile.created_by.username}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Editable Profile */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Edit Profile</Typography>
            <Divider sx={{ mb: 3 }} />
            
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="full_name"
                    name="full_name"
                    label="Full Name"
                    value={formik.values.full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                    helperText={formik.touched.full_name && formik.errors.full_name}
                    disabled={loading}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    value={formik.values.phone || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading || !formik.dirty}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Update Profile'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserProfile;